import React, { useEffect, useRef, useState } from "react";
import { Search, Upload, Library, Loader2, ImageOff, Check, Sparkles, AlertTriangle, Wand2, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, apiErrorMessage } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">{children}</div>
);

const Thumb = ({ src, label, busy, onClick, testid, badge }) => (
  <button type="button" onClick={onClick} disabled={busy} data-testid={testid}
    className="group relative aspect-[4/3] overflow-hidden rounded-md border bg-muted transition-shadow hover:shadow-md disabled:opacity-60">
    <img src={src} alt={label || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      onError={(e) => { e.currentTarget.style.opacity = 0.15; }} />
    {badge && (
      <span className="absolute left-1 top-1 rounded bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">{badge}</span>
    )}
    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
      {busy ? <Loader2 className="h-6 w-6 animate-spin text-white" /> : <Check className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />}
    </div>
  </button>
);

const SOURCE_BADGE = { ai: "IA" };

const SELECT_CLS = "h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground";

export default function ImagePicker({ open, onOpenChange, onSelect, orientation = "horizontal", multiple = false, title = "Añadir imagen", defaultQuery = "" }) {
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState(defaultQuery);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [orient, setOrient] = useState(orientation === "vertical" ? "vertical" : orientation === "horizontal" ? "horizontal" : "all");
  const [sort, setSort] = useState("relevance");
  const [editorial, setEditorial] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [status, setStatus] = useState(null);
  const [bank, setBank] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const fileRef = useRef();

  useEffect(() => { if (open) { setQ(defaultQuery); setResults([]); setTotal(0); setPage(1); setPreview(null); setAiResult(null); loadStatus(); } }, [open]); // eslint-disable-line
  useEffect(() => { if (open && tab === "bank") loadBank(""); }, [open, tab]); // eslint-disable-line
  useEffect(() => { if (open && defaultQuery) doSearch(defaultQuery, { reset: true }); }, [open]); // eslint-disable-line

  const loadStatus = async () => { try { setStatus((await api.get("/media/stock/status")).data); } catch { /* ignore */ } };
  const loadBank = async (query) => {
    setBankLoading(true);
    try { setBank((await api.get(`/media/bank`, { params: { q: query } })).data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setBankLoading(false); }
  };

  const doSearch = async (query, opts = {}) => {
    const term = (query ?? q).trim();
    if (term.length < 2) return;
    const nextPage = opts.reset ? 1 : (opts.page || 1);
    setSearching(true);
    try {
      const params = { q: term, page: nextPage, sort, editorial };
      if (orient !== "all") params.orientation = orient;
      const { data } = await api.get("/media/stock/search", { params });
      const items = data.items || [];
      setResults((prev) => (nextPage > 1 ? [...prev, ...items] : items));
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSearching(false); }
  };

  const searchSimilar = (item) => {
    const kws = (item.keywords || []).slice(0, 4).join(" ").trim()
      || (item.tags || []).filter((t) => t !== "IA").slice(0, 4).join(" ").trim()
      || (item.description || "").trim();
    if (!kws) { toast.error("Sin etiquetas para buscar similares"); return; }
    setPreview(null); setTab("search"); setQ(kws); doSearch(kws, { reset: true });
  };

  const openPreview = (item, kind) => setPreview({ ...item, _kind: kind });

  const aiGenerate = async () => {
    const p = aiPrompt.trim();
    if (p.length < 3) { toast.error("Escribe un prompt más descriptivo"); return; }
    setAiBusy(true); setAiResult(null);
    try {
      const params = { prompt: p };
      if (orient !== "all") params.orientation = orient;
      const { data } = await api.post("/media/ai/generate", params);
      setAiResult(data); toast.success("Imagen generada con IA");
    } catch (e) { toast.error(apiErrorMessage(e, "No se pudo generar la imagen")); }
    finally { setAiBusy(false); }
  };

  const emit = (url) => { onSelect(url); if (!multiple) onOpenChange(false); };

  const license = async (item) => {
    setBusyId(item.id);
    try {
      const { data } = await api.post("/media/stock/license", { image_id: item.id, description: item.description, tags: q ? [q.trim()] : [] });
      toast.success("Imagen licenciada y añadida");
      emit(data.url); loadStatus(); setPreview(null);
    } catch (e) {
      if (e?.response?.status === 429) toast.error(e.response.data.detail || "Tope mensual alcanzado");
      else if (item.editorial) toast.error("Las imágenes editoriales no están incluidas en tu suscripción (standard). Elige una comercial o súbela manualmente.");
      else toast.error(apiErrorMessage(e, "No se pudo licenciar"));
    } finally { setBusyId(null); }
  };

  const upload = async (files) => {
    for (const file of Array.from(files || [])) {
      setBusyId("upload");
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`«${file.name}» subida`); emit(data.url);
      } catch (e) { toast.error(apiErrorMessage(e, "No se pudo subir")); }
      finally { setBusyId(null); }
      if (!multiple) break;
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const left = status?.month_left;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="image-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />{title}</DialogTitle>
          <DialogDescription>
            Busca en Shutterstock, sube tus fotos o reutiliza el banco. {multiple ? "Puedes añadir varias." : "Elige una."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between gap-2">
            <TabsList data-testid="image-picker-tabs">
              <TabsTrigger value="search" data-testid="tab-search"><Search className="mr-1.5 h-4 w-4" />Shutterstock</TabsTrigger>
              <TabsTrigger value="ai" data-testid="tab-ai"><Wand2 className="mr-1.5 h-4 w-4" />Crear con IA</TabsTrigger>
              <TabsTrigger value="upload" data-testid="tab-upload"><Upload className="mr-1.5 h-4 w-4" />Subir</TabsTrigger>
              <TabsTrigger value="bank" data-testid="tab-bank"><Library className="mr-1.5 h-4 w-4" />Banco</TabsTrigger>
            </TabsList>
            {status?.configured && typeof left === "number" && (
              <Badge variant={left <= 20 ? "destructive" : "secondary"} className="gap-1" data-testid="stock-quota-badge">
                {left <= 20 && <AlertTriangle className="h-3 w-3" />}{left} de {status.month_cap} este mes
              </Badge>
            )}
          </div>

          <TabsContent value="search" className="mt-4">
            <form onSubmit={(e) => { e.preventDefault(); doSearch(q, { reset: true }); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej: Rome Colosseum, Kyoto temple…" className="pl-8" data-testid="stock-search-input" />
              </div>
              <Button type="submit" disabled={searching || q.trim().length < 2} data-testid="stock-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
              </Button>
            </form>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={orient} onChange={(e) => { setOrient(e.target.value); if (results.length) doSearch(q, { reset: true }); }} className={SELECT_CLS} data-testid="stock-orient-select">
                <option value="all">Todas las orientaciones</option>
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
              </select>
              <select value={sort} onChange={(e) => { setSort(e.target.value); if (results.length) doSearch(q, { reset: true }); }} className={SELECT_CLS} data-testid="stock-sort-select">
                <option value="relevance">Más relevantes</option>
                <option value="popular">Más populares</option>
                <option value="newest">Más nuevas</option>
              </select>
              <label className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground" data-testid="stock-editorial-toggle">
                <input type="checkbox" checked={editorial} onChange={(e) => { setEditorial(e.target.checked); if (results.length) doSearch(q, { reset: true }); }} className="h-4 w-4 rounded border-input" />
                Incluir editorial
              </label>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">La foto se licencia (consume 1 crédito) solo al seleccionarla. Las del banco son gratis. Las <span className="font-medium text-amber-600">editoriales</span> son solo para uso interno y no siempre se pueden licenciar.</p>
            <div className="mt-3 max-h-[46vh] overflow-y-auto pr-1">
              {searching && page === 1 ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : results.length ? (
                <><p className="mb-2 text-xs text-muted-foreground">{total.toLocaleString()} resultados</p>
                  <Grid>{results.map((it, i) => (
                    <Thumb key={`${it.id}-${i}`} src={it.preview_url} label={it.description} badge={it.editorial ? "Editorial" : null} busy={busyId === it.id} onClick={() => openPreview(it, "stock")} testid={`stock-result-${it.id}`} />
                  ))}</Grid>
                  {results.length < total && (
                    <div className="mt-3 flex justify-center">
                      <Button variant="outline" size="sm" disabled={searching} onClick={() => doSearch(q, { page: page + 1 })} data-testid="stock-load-more-btn">
                        {searching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Cargar más
                      </Button>
                    </div>
                  )}</>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground"><ImageOff className="h-8 w-8" /><p className="text-sm">Busca fotos por destino, ciudad o experiencia.</p></div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="ai" className="mt-4" data-testid="ai-tab">
            <div className="rounded-md border bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />Uso <strong>decorativo</strong>: la IA crea una imagen de atmósfera, <strong>no es una foto real del lugar</strong>. No la uses como prueba de un servicio concreto.
            </div>
            <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={3} data-testid="ai-prompt-input"
              placeholder="Describe la imagen. Ej: Costa Amalfitana al atardecer, tonos cálidos, estilo editorial de viaje, sin texto"
              className="mt-2 w-full rounded-md border border-input bg-background p-2 text-sm" />
            <div className="mt-2 flex items-center gap-2">
              <Button onClick={aiGenerate} disabled={aiBusy || aiPrompt.trim().length < 3} data-testid="ai-generate-btn">
                {aiBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}Generar imagen
              </Button>
              <span className="text-xs text-muted-foreground">Orientación: {orient === "all" ? "libre" : orient}</span>
            </div>
            <div className="mt-3">
              {aiBusy ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /><p className="text-sm">Generando… (10–20 s)</p></div>
              ) : aiResult ? (
                <div className="space-y-3">
                  <img src={imgUrl(aiResult.url)} alt="IA" className="max-h-[38vh] w-full rounded-md border object-contain" data-testid="ai-result-img" />
                  <div className="flex gap-2">
                    <Button onClick={() => emit(aiResult.url)} data-testid="ai-use-btn"><Check className="mr-1.5 h-4 w-4" />Usar esta imagen</Button>
                    <Button variant="outline" onClick={aiGenerate} disabled={aiBusy} data-testid="ai-regenerate-btn"><RefreshCw className="mr-1.5 h-4 w-4" />Generar otra</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-14 text-muted-foreground"><Wand2 className="h-8 w-8" /><p className="text-sm">Escribe un prompt y genera una imagen. Se guarda en tu banco (no gasta cuota de Shutterstock).</p></div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
              className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed py-16 text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="upload-dropzone">
              {busyId === "upload" ? <Loader2 className="h-8 w-8 animate-spin" /> : <Upload className="h-8 w-8" />}
              <p className="text-sm font-medium">Arrastra una imagen o haz clic para elegir</p>
              <p className="text-xs">JPG, PNG, WEBP o GIF · máx. 12 MB</p>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="upload-input" />
          </TabsContent>

          <TabsContent value="bank" className="mt-4">
            <form onSubmit={(e) => { e.preventDefault(); loadBank(q); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en tu banco de imágenes…" className="pl-8" data-testid="bank-search-input" />
              </div>
              <Button type="submit" variant="outline" data-testid="bank-search-btn">Buscar</Button>
            </form>
            <div className="mt-3 max-h-[46vh] overflow-y-auto pr-1">
              {bankLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : bank.length ? (
                <Grid>{bank.map((b) => (
                  <Thumb key={b.id} src={imgUrl(b.url)} label={b.description} badge={SOURCE_BADGE[b.source]} busy={false} onClick={() => openPreview(b, "bank")} testid={`bank-item-${b.id}`} />
                ))}</Grid>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground"><Library className="h-8 w-8" /><p className="text-sm">Tu banco está vacío. Las fotos licenciadas o subidas aparecerán aquí para reutilizarlas gratis.</p></div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {preview && (
          <div className="absolute inset-0 z-50 flex flex-col rounded-lg bg-background p-4" data-testid="stock-preview-overlay">
            <div className="mb-2 flex items-center justify-between">
              <button onClick={() => setPreview(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="preview-back-btn"><ArrowLeft className="h-4 w-4" />Volver</button>
              <div className="flex items-center gap-2">
                {preview._kind === "bank" && preview.source === "ai" && <span className="rounded bg-amber-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Imagen IA</span>}
                {preview.editorial && <span className="rounded bg-amber-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Editorial</span>}
              </div>
            </div>
            <div className="relative flex-1 overflow-hidden rounded-md border bg-muted">
              <img src={preview._kind === "bank" ? imgUrl(preview.url) : preview.preview_url} alt={preview.description} className="h-full w-full object-contain" data-testid="preview-image" />
            </div>
            {preview.description && <p className="mt-2 line-clamp-2 text-sm text-foreground" data-testid="preview-desc">{preview.description}</p>}
            {(() => {
              const cats = preview.categories || [];
              const kws = [...new Set([...(preview.keywords || []), ...((preview.tags || []).filter((t) => t !== "IA"))])];
              if (!cats.length && !kws.length) return null;
              return (
                <div className="mt-2 max-h-16 overflow-y-auto" data-testid="preview-tags">
                  <div className="flex flex-wrap gap-1">
                    {cats.map((c, i) => <Badge key={`c${i}`} variant="secondary" className="text-[10px]">{c}</Badge>)}
                    {kws.slice(0, 16).map((k, i) => <Badge key={`k${i}`} variant="outline" className="text-[10px] font-normal">{k}</Badge>)}
                  </div>
                </div>
              );
            })()}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {preview._kind === "bank" ? (
                <Button onClick={() => { emit(preview.url); setPreview(null); }} data-testid="preview-use-btn"><Check className="mr-1.5 h-4 w-4" />Usar esta imagen</Button>
              ) : (
                <Button onClick={() => license(preview)} disabled={busyId === preview.id} data-testid="preview-license-btn">
                  {busyId === preview.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Licenciar y usar
                </Button>
              )}
              <Button variant="outline" onClick={() => searchSimilar(preview)} data-testid="preview-similar-btn"><RefreshCw className="mr-1.5 h-4 w-4" />Ver similares</Button>
              {preview._kind !== "bank" && <span className="ml-auto text-xs text-muted-foreground">Consume 1 crédito al licenciar</span>}
              {preview._kind === "bank" && <span className="ml-auto text-xs text-muted-foreground">Del banco · gratis</span>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
